import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@features/auth/store/authStore';
import { notificationLink } from './notificationApi';
import {
  useNotificationPreferences,
  useNotifications,
  useReadNotification,
} from './notificationHooks';

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);
  const inbox = useNotifications();
  const preferences = useNotificationPreferences();
  const mark = useReadNotification();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [started] = useState(() => Date.now());
  const shown = useRef(new Set<string>());
  useEffect(() => {
    const p = preferences.data;
    if (
      !userId ||
      !p?.systemDesktopEnabled ||
      !p.desktopEnabled ||
      typeof Notification === 'undefined' ||
      Notification.permission !== 'granted'
    )
      return;
    const fresh = (inbox.data?.items ?? [])
      .filter(
        (item) =>
          !item.readAt &&
          !shown.current.has(item.id) &&
          new Date(item.createdAt).getTime() > started &&
          Date.now() - new Date(item.createdAt).getTime() < 120000 &&
          (item.type === 'assignment' ? p.assignmentsEnabled : p.negativeFeedbackEnabled),
      )
      .slice(0, 5);
    for (const item of fresh) {
      shown.current.add(item.id);
      try {
        const key = `helix-desktop:${userId}`;
        const previous = JSON.parse(localStorage.getItem(key) ?? '[]') as string[];
        if (previous.includes(item.id)) continue;
        localStorage.setItem(key, JSON.stringify([...previous.slice(-199), item.id]));
        const popup = new Notification(item.title, { body: item.body, tag: `helix:${item.id}` });
        popup.onclick = () => {
          window.focus();
          void navigate(notificationLink(item.id));
          popup.close();
        };
      } catch {
        /* Browser/OS permission or storage restrictions do not affect the durable inbox. */
      }
    }
  }, [inbox.data, navigate, preferences.data, started, userId]);
  return (
    <>
      <Tooltip title={t('common.notifications')}>
        <IconButton
          aria-label={t('common.notifications')}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ flexShrink: 0 }}
        >
          <Badge
            color="error"
            badgeContent={inbox.data?.unreadCount ?? 0}
            max={99}
            overlap="circular"
          >
            <NotificationsNoneRoundedIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 390, maxWidth: 'calc(100vw - 24px)' } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{t('notification.title')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('notification.unreadCount', { count: inbox.data?.unreadCount ?? 0 })}
          </Typography>
        </Box>
        <Divider />
        {inbox.isError ? (
          <MenuItem onClick={() => void inbox.refetch()}>{t('notification.loadError')}</MenuItem>
        ) : !inbox.data?.items.length ? (
          <MenuItem disabled>{t('common.noNotifications')}</MenuItem>
        ) : (
          inbox.data.items.slice(0, 6).map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => {
                setAnchor(null);
                void navigate(notificationLink(item.id));
              }}
              sx={{ alignItems: 'flex-start', whiteSpace: 'normal', py: 1.25, gap: 1 }}
            >
              <Box
                sx={{
                  mt: 0.75,
                  width: 7,
                  height: 7,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: item.readAt ? 'transparent' : 'primary.main',
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: item.readAt ? 400 : 700 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {new Date(item.createdAt).toLocaleString(i18n.language)}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
        <Divider />
        <Stack direction="row" sx={{ px: 1, py: 0.5, flexWrap: 'wrap' }}>
          <Button
            size="small"
            disabled={!inbox.data?.unreadCount || mark.isPending}
            onClick={() => mark.mutate({})}
          >
            {t('notification.readAll')}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setAnchor(null);
              void navigate('/notifications');
            }}
          >
            {t('notification.viewAll')}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setAnchor(null);
              void navigate('/notification-preferences');
            }}
          >
            {t('notification.preferences')}
          </Button>
        </Stack>
        {mark.isError && (
          <Typography color="error" sx={{ px: 2, fontSize: 12 }}>
            {t('common.actionFailed')}
          </Typography>
        )}
      </Menu>
    </>
  );
}
